import React from "react";
import { FieldGroupRow, FieldGroupRowStatus } from "../../../models/intake";
import FormConstants from "./DataEntryFieldsConstants";
import {
    writeFieldToForm, ClassicFieldMeta, classicFieldErrorMessage, toDisplayDate, isUtcMidnightDate,
} from "./formFieldHelpers";
import DateField from "../../../common/formFields/DateField";
import { DEFAULT_MIN_DATE } from "../../../common/constants/ApiConstants";
import { isAuthDateRequired } from "./validations/localValidations";

export interface AuthorizationDateFieldProps {
    /** Currently selected Authorization date (read from DATA_ENTRY_FIELDS_FORM). */
    readonly value?: Date | string;
    /** transactionDetails.requestPurpose?.id - drives isAuthDateRequired, same source the classic pane's validator already reads (not the live form purpose value). */
    readonly requestPurposeId?: number;
    /** Hides the field entirely - classic pane's requestType != DISCLOSURE_ACCOUNTING gate. */
    readonly visible: boolean;
    readonly dispatch: Function;
    readonly formName?: string;
    /**
     * Extra, non-form side effect the caller wants to run on change (the
     * classic pane's autosave PATCH). The base DATA_ENTRY_FIELDS_FORM write
     * is always handled by this component itself via writeFieldToForm -
     * callers should NOT also dispatch a redundant form write here (same
     * rule RequestTypePurposeField's onRequestTypeChange established).
     */
    readonly onAuthorizationDateChange?: (newValue?: Date | string) => void;
    /** AB#38460: unusable text to keep on screen, and the channel that reports it. Forwarded verbatim to DateField - see its own docblocks. */
    readonly invalidText?: string;
    readonly onInvalidText?: (text?: string, committed?: boolean) => void;
    /** AB#38460: forwarded verbatim to DateField - see its docblock. */
    readonly resyncKey?: string | number;
    /**
     * Real redux-form validation state, threaded down from
     * DataEntryDetailsRightPane.tsx (Spec #34102 / F1d regression fix -
     * restores "Authorization date is required."/"...must not be a future
     * date." text on a blocked Next-click, lost when this component stopped
     * being wrapped in a real <Field> during the F1a extraction). Omitted by
     * Quick Entry's own call site.
     */
    readonly meta?: ClassicFieldMeta;
}

/**
 * Shared Authorization Date field (Spec #34098 / F1a). Rendered by both the
 * classic RightPane Details tab and the new Quick Entry row so both views
 * write into - and read from - the same DATA_ENTRY_FIELDS_FORM redux-form
 * instance via the shared writeFieldToForm helper.
 */
const AuthorizationDateField: React.FC<AuthorizationDateFieldProps> = (props) => {
    if (!props.visible) {
        return null;
    }

    const formName = props.formName ?? FormConstants.DATA_ENTRY_FIELDS_FORM;
    const required = isAuthDateRequired(props.requestPurposeId);

    const onChange = (value?: Date | string) => {
        writeFieldToForm(props.dispatch, FormConstants.AUTHORIZATION_DATE, value, formName);
        props.onAuthorizationDateChange?.(value);
    };

    return (
        <DateField
            label="Authorization date"
            placeholder="mm/dd/yyyy"
            minDate={DEFAULT_MIN_DATE}
            maxDate={new Date()}
            required={required}
            errMsg={classicFieldErrorMessage(props.meta)}
            invalidText={props.invalidText}
            onInvalidText={props.onInvalidText}
            resyncKey={props.resyncKey}
            onChange={onChange}
            // AB#37843: display-only normalization - see toDisplayDate. The write
            // path (onChange above) is untouched; only what the picker SHOWS changes.
            value={toDisplayDate(props.value)}
            // Restores mirrorData(FormConstants.AUTHORIZATION_DATE, date) firing
            // on change/blur (DateField.tsx's own onChangeDate/onBlur read this
            // `name` prop) - keeps transactionDetails.date.authorization in sync,
            // same as before the F1a extraction (Spec #34102 / F1d regression fix).
            name={FormConstants.AUTHORIZATION_DATE}
        />
    );
};

export default AuthorizationDateField;

// Exported so DataEntryDetails.tsx can key its Quick Entry renderRowContent
// special-case off the same constant used to build the row.
export const AUTHORIZATION_DATE_ROW_ID = "authorizationDate";

/**
 * MM/DD/YYYY for the row's read-only display. Handles both a Date (extracted /
 * seeded) and a date string (yyyy-MM-dd or full ISO). Date-only strings are
 * reformatted by parts to avoid a timezone day-shift. AB#37982: a Date branches on
 * isUtcMidnightDate exactly like formatShortFieldGroupDate (formFieldHelpers.ts) - a
 * UTC-midnight Date (AI-extracted) reads its own UTC calendar day; any other Date
 * (specialist-picked/typed, local midnight) reads its own LOCAL calendar day.
 * Unconditional toISOString() shifted a picked/typed date to the PREVIOUS day in any
 * timezone AHEAD of UTC.
 */
function formatAuthDateValue(v: Date | string): string {
    let isoDate: string | undefined;
    if (typeof v === "string") {
        isoDate = v;
    } else if (v instanceof Date && !isNaN(v.getTime())) {
        isoDate = isUtcMidnightDate(v)
            ? v.toISOString().substring(0, 10)
            : `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, "0")}-${String(v.getDate()).padStart(2, "0")}`;
    }
    const m = isoDate?.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[2]}/${m[3]}/${m[1]}` : String(v);
}

/** Value-based equality for Date | string authorization dates. */
function sameAuthDate(a: Date | string | undefined, b: Date | string | undefined): boolean {
    if (!a || !b) {
        return false;
    }
    const ta = a instanceof Date ? a.getTime() : new Date(a).getTime();
    const tb = b instanceof Date ? b.getTime() : new Date(b).getTime();
    return !isNaN(ta) && ta === tb;
}

/**
 * Builds the FieldGroupRow for the Authorization Date row. Required-ness
 * mirrors the classic pane's own isAuthDateRequired(requestPurposeId) rule;
 * NeedsEntry when required-and-empty, EmptyNotRequired when optional-and-
 * empty, Confirmed once a value exists.
 *
 * `aiValue` is the AI-extracted authorization date (requestAnalysis.data.
 * authorizationDate); DataEntryDetails seeds it into the form field, so the row
 * shows it and reads as AI-pre-selected (green) until the specialist edits it.
 */
export function mapAuthorizationDateToFieldGroupRow(
    badgeNumber: number,
    requestPurposeId: number | undefined,
    formValue: Date | string | undefined,
    aiValue?: Date | string,
): FieldGroupRow {
    const hasValue = formValue !== undefined && formValue !== null && formValue !== "";
    const required = isAuthDateRequired(requestPurposeId);

    return {
        id: AUTHORIZATION_DATE_ROW_ID,
        badgeNumber,
        status: hasValue
            ? FieldGroupRowStatus.Confirmed
            : (required ? FieldGroupRowStatus.NeedsEntry : FieldGroupRowStatus.EmptyNotRequired),
        label: "Authorization Date",
        // F1c (#34101) seeds the extracted authorization date and marks the row
        // AI-pre-selected when the current value still matches the extracted one.
        // formatAuthDateValue handles both Date and the string form the API
        // returns (the same raw-toString bug F1d's formatFieldGroupDate fixed).
        value: hasValue ? formatAuthDateValue(formValue!) : null,
        isAiPreSelected: Boolean(aiValue) && sameAuthDate(formValue, aiValue),
    };
}
